import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import _debounce from 'lodash/debounce';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import EmptyData from '../../../../components/UI/EmptyData';
import { TableSkeleton } from '../../../../components/UI/Skeleton/Skeleton';

import { useIsLoggedInQuery } from '../../../../redux/apis/authApi';
import { useGetHRXAttendanceListQuery } from '../../../../redux/apis/attendanceApi';
import {
  setCurrentPage,
  setIsNext,
  setRange,
  setSessionShow,
  setSelectedSession,
  resetHrxAttendanceState
} from '../../../../redux/Slice/hrxAttendanceSlice';

import { formatDate, formatterTime, skipRow } from '../../../../utils/handler';
import { ADMIN_HR_ROLES, GLOBAL } from '../../../../utils/constant/global';
import { selectDisplayTimezone } from '../../../../redux/utils/apiLocality';
import { formatTimestampInTimezone } from '../../../../utils/timedoctorAttendanceHelpers';
import {
  getHrxDisplayCheckOutTime,
  isHrxCheckedOut,
  isHrxSessionActive
} from '../../../../utils/attendanceTimelineHelpers';
import SessionDialog from './AttendanceDetailDialog';

const AttendanceTable = ({ userId }) => {
  const dispatch = useDispatch();
  const { data: isLoggedInData } = useIsLoggedInQuery();
  const state = useSelector((state) => state.hrxAttendance);
  const range = useSelector((state) => state?.hrxAttendance?.range) || [null, null];
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';

  const [debouncedRange, setDebouncedRange] = useState(range);
  const [hasDateSelection, setHasDateSelection] = useState(
    () => Boolean(range?.[0]) || Boolean(range?.[1])
  );
  const [activeKeys, setActiveKeys] = useState([]);

  const roleName = isLoggedInData?.data?.hrxRole?.roleName;
  const isAdminOrHr = ADMIN_HR_ROLES.includes(roleName);
  const queryUserId = isAdminOrHr ? userId || undefined : isLoggedInData?.data?._id;

  const { data, isLoading, isFetching, refetch } = useGetHRXAttendanceListQuery({
    startDate: debouncedRange?.[0] ? moment.utc(debouncedRange?.[0]).valueOf() : null,
    endDate: debouncedRange?.[1] ? moment.utc(debouncedRange?.[1]).valueOf() : null,
    search: state.search,
    skip: skipRow(state.currentPage, state.rowsPerPage),
    limit: state.rowsPerPage,
    sort: state.sortField,
    sortType: state.sortDirection ? GLOBAL.DESC : GLOBAL.ASC,
    ...(queryUserId ? { userId: queryUserId } : {})
  });

  useEffect(() => {
    refetch();
    return () => {
      dispatch(resetHrxAttendanceState());
    };
  }, []);

  useEffect(() => {
    if (data) {
      dispatch(setIsNext(data?.isNext));
    }
  }, [data, dispatch]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [debouncedRange, userId, dispatch]);

  useEffect(() => {
    setHasDateSelection(Boolean(range?.[0]) || Boolean(range?.[1]));
  }, [range]);

  useEffect(() => {
    const handler = _debounce(() => {
      if (range[0] && range[1]) {
        setDebouncedRange(range);
      } else if (range[0] === null && range[1] === null) {
        setDebouncedRange([null, null]);
      }
    }, 1000);

    handler();
    return () => handler.cancel();
  }, [range]);

  const employeeGroups = useMemo(() => {
    const list = Array.isArray(data?.data) ? data.data : [];
    if (!list.length) return [];

    const looksGrouped = list[0]?.attendance !== undefined || list[0]?.summary !== undefined;

    if (looksGrouped) {
      return list.map((item) => ({
        userId: item.userId,
        userName: item.userName || 'Employee',
        summary: item.summary || null,
        attendance: Array.isArray(item.attendance) ? item.attendance : []
      }));
    }

    // Legacy flat day rows — group by employee
    const map = new Map();
    list.forEach((row) => {
      const key = row.userId || row.userName || 'self';
      if (!map.has(key)) {
        map.set(key, {
          userId: key,
          userName: row.userName || 'Employee',
          summary: null,
          attendance: []
        });
      }
      map.get(key).attendance.push(row);
    });
    return Array.from(map.values());
  }, [data]);

  useEffect(() => {
    if (!employeeGroups.length) {
      setActiveKeys([]);
      return;
    }
    setActiveKeys((prev) => {
      if (prev.length) return prev;
      return [String(employeeGroups[0].userId)];
    });
  }, [employeeGroups]);

  const isWfhLeaveType = (type) => {
    const t = String(type || '')
      .trim()
      .toUpperCase();
    return (
      t === 'WFH' || t === 'REMOTE' || t.includes('WORK FROM HOME') || t.includes('WORK_FROM_HOME')
    );
  };

  const getLeaveTypesFromRow = (row) => {
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    return leaves.map((l) => l?.leaveType).filter(Boolean);
  };

  const wfhInfoFromRow = (row) => {
    const leaveTypes = getLeaveTypesFromRow(row);
    if (leaveTypes.some(isWfhLeaveType)) {
      return { label: 'WFH' };
    }

    const alert = String(row?.absenceAlert || '')
      .trim()
      .toUpperCase();
    const status = String(row?.currentStatus || '')
      .trim()
      .toUpperCase();
    if (isWfhLeaveType(alert) || alert.includes('WFH') || alert.includes('REMOTE')) {
      return { label: 'WFH' };
    }
    if (status === 'REMOTE' || status.includes('WFH')) {
      return { label: 'WFH' };
    }

    return null;
  };

  const dutyLabelFromRow = (row) => {
    const lastIn =
      Array.isArray(row?.lastBreakIn) && row.lastBreakIn.length
        ? Number(row.lastBreakIn[row.lastBreakIn.length - 1])
        : null;
    const lastOut =
      Array.isArray(row?.lastBreakOut) && row.lastBreakOut.length
        ? Number(row.lastBreakOut[row.lastBreakOut.length - 1])
        : null;

    const hasCheckIn = Boolean(row?.checkInTime);
    const statusRaw = String(row?.currentStatus || '').trim();
    const statusLower = statusRaw.toLowerCase();

    if (!hasCheckIn) {
      const upper = statusRaw.toUpperCase();
      if (
        !statusRaw ||
        upper.includes('LEAVE') ||
        upper.includes('LATE') ||
        upper.includes('EARLY')
      ) {
        return { text: '—', variant: 'secondary' };
      }
      return { text: statusRaw, variant: 'secondary' };
    }

    if (isHrxCheckedOut(row)) {
      return { text: 'Checked Out', variant: 'secondary' };
    }

    if (isHrxSessionActive(row) || statusLower.includes('on break')) {
      const onBreak =
        statusLower.includes('on break') ||
        (Number.isFinite(lastIn) && (!Number.isFinite(lastOut) || lastIn > lastOut));

      if (onBreak) {
        return { text: 'On Break', variant: 'warning' };
      }
      return { text: 'Checked In', variant: 'success' };
    }

    if (Number.isFinite(lastIn) && (!Number.isFinite(lastOut) || lastIn > lastOut)) {
      return { text: 'On Break', variant: 'warning' };
    }
    return { text: 'Checked In', variant: 'success' };
  };

  const getNonWfhLeaveTypes = (row) =>
    getLeaveTypesFromRow(row)
      .filter((t) => !isWfhLeaveType(t))
      .filter((v, i, a) => a.indexOf(v) === i);

  const isLateOrEarlyLeaveType = (type) => {
    const t = String(type || '')
      .trim()
      .toUpperCase()
      .replace(/[_-]+/g, ' ');
    return (
      t === 'LATE ARRIVAL' ||
      t === 'EARLY EXIT' ||
      t.includes('LATE ARRIVAL') ||
      t.includes('EARLY EXIT')
    );
  };

  const isLateOrEarlyViolationLeave = (leave) => {
    if (!leave) return false;
    if (isLateOrEarlyLeaveType(leave.leaveType)) return true;
    const vt = String(leave.violationType || '')
      .trim()
      .toUpperCase();
    return vt === 'LATE_ARRIVAL' || vt === 'EARLY_EXIT';
  };

  const getRegularLeaveTypes = (row) => {
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    return getNonWfhLeaveTypes(row).filter((type) => {
      const match = leaves.find((l) => l?.leaveType === type);
      if (match && isLateOrEarlyViolationLeave(match)) return false;
      return !isLateOrEarlyLeaveType(type);
    });
  };

  const getLateEarlyLeaveTypes = (row) => {
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    return getNonWfhLeaveTypes(row).filter((t) => {
      const match = leaves.find((l) => l?.leaveType === t);
      return (match && isLateOrEarlyViolationLeave(match)) || isLateOrEarlyLeaveType(t);
    });
  };

  const formatLeaveTypeLabel = (row, types) => {
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    const labels = (types || []).map((type) => {
      const match = leaves.find((l) => l?.leaveType === type);
      if (match?.isHalfDay) {
        const session =
          match.sessionType ||
          (Number(match.leaveSession) === 1
            ? 'First Half'
            : Number(match.leaveSession) === 2
              ? 'Second Half'
              : 'Half Day');
        return `${type} · ${session}`;
      }
      return type;
    });
    if (labels.length) return labels.join(', ');
    return row?.absenceAlert || 'On Leave';
  };

  const leaveLabelFromRow = (row) => {
    const isOnLeave = Boolean(row?.leaveInfo?.isOnLeave);
    const regularLeaveTypes = getRegularLeaveTypes(row);
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    const isHalfDay =
      /half day/i.test(String(row?.absenceAlert || '')) ||
      leaves.some((l) => l?.isHalfDay && !isWfhLeaveType(l?.leaveType));

    if (!regularLeaveTypes.length) return null;

    if (!isOnLeave) {
      const alert = String(row?.absenceAlert || '').toUpperCase();
      if (
        (alert.includes('LEAVE') || alert.includes('ON LEAVE')) &&
        !alert.includes('WFH') &&
        !alert.includes('REMOTE')
      ) {
        return {
          label: formatLeaveTypeLabel(row, regularLeaveTypes),
          types: regularLeaveTypes,
          isHalfDay
        };
      }
      return null;
    }

    return {
      label: formatLeaveTypeLabel(row, regularLeaveTypes),
      types: regularLeaveTypes,
      isHalfDay
    };
  };

  const sessionHalvesFromRow = (row) => {
    const first = String(row?.sessionInfo?.firstHalf || '').trim();
    const second = String(row?.sessionInfo?.secondHalf || '').trim();
    if (!first && !second) return null;

    const hasLeaveHalf = /leave/i.test(first) || /leave/i.test(second);
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    const isHalfDay =
      /half day/i.test(String(row?.absenceAlert || '')) || leaves.some((l) => l?.isHalfDay);

    // Skip noisy Present/Absent pairs on normal duty days
    if (!hasLeaveHalf && !isHalfDay) return null;

    // Skip identical full-day leave/WFH halves (e.g. On Leave / On Leave)
    if (first && second && first.toLowerCase() === second.toLowerCase() && !isHalfDay) {
      return null;
    }

    return { firstHalf: first || '—', secondHalf: second || '—' };
  };

  const sessionHalfTone = (value) => {
    const v = String(value || '').toLowerCase();
    if (v.includes('present')) return 'present';
    if (v.includes('leave')) return 'leave';
    if (v.includes('absent')) return 'absent';
    return 'neutral';
  };

  const hasViolationLeaveFromRow = (row) => Boolean(row?.leaveInfo?.hasViolationLeave);

  const getViolationLeaveMeta = (row) => {
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    const violationLeave = leaves.find((l) => l?.isViolationLeave) || leaves[0];
    const violationType = String(violationLeave?.violationType || '')
      .trim()
      .toUpperCase();
    if (violationType === 'EARLY_EXIT') return { typeClass: 'exit', violationType };
    if (violationType === 'LATE_ARRIVAL') return { typeClass: 'late', violationType };
    return { typeClass: 'default', violationType };
  };

  const renderStatusPill = (pill) => (
    <span className={`attendance-status__pill attendance-status__pill--${pill.kind}`}>
      {pill.icon ? (
        <span className="attendance-status__icon" aria-hidden>
          <i className={pill.icon} />
        </span>
      ) : null}
      <span>{pill.label}</span>
    </span>
  );

  const renderViolationLeaveStatus = (row) => {
    const { typeClass } = getViolationLeaveMeta(row);

    return (
      <div
        className={`attendance-violation-status attendance-violation-status--${typeClass}`}
        title="Violation Leave Cut"
      >
        <span className="attendance-violation-status__icon" aria-hidden>
          <i className="bi bi-exclamation-octagon-fill" />
        </span>
        <span className="attendance-violation-status__copy">
          <span className="attendance-violation-status__kicker">Violation</span>
          <span className="attendance-violation-status__label">Leave Cut</span>
        </span>
      </div>
    );
  };

  const renderLeaveStatusPill = (label, row) => {
    if (hasViolationLeaveFromRow(row)) {
      return renderViolationLeaveStatus(row);
    }

    return (
      <span className="attendance-status__pill attendance-status__pill--leave" title={label}>
        <span>{label}</span>
      </span>
    );
  };

  const statusPillFromRow = (row) => {
    const wfh = wfhInfoFromRow(row);
    const leave = leaveLabelFromRow(row);
    const lateEarly = getLateEarlyLeaveTypes(row);
    const sessions = sessionHalvesFromRow(row);

    if (wfh && leave) {
      return {
        kind: 'wfh-leave',
        wfhLabel: wfh.label,
        leaveLabel: leave.label,
        types: leave.types,
        isHalfDay: leave.isHalfDay,
        sessions,
        lateEarly
      };
    }

    if (wfh) {
      return { kind: 'wfh', label: wfh.label, sessions, lateEarly };
    }

    if (leave) {
      return {
        kind: 'leave',
        label: leave.label,
        types: leave.types,
        isHalfDay: leave.isHalfDay,
        sessions,
        lateEarly
      };
    }

    const s = dutyLabelFromRow(row);
    let dutyPill;
    if (s.text === 'Checked In')
      dutyPill = { kind: 'duty', label: s.text, icon: 'bi bi-lightning-charge-fill' };
    else if (s.text === 'On Break')
      dutyPill = { kind: 'break', label: s.text, icon: 'bi bi-cup-hot-fill' };
    else if (s.variant === 'secondary')
      dutyPill = {
        kind: 'out',
        label: s.text || 'Checked Out',
        icon: 'bi bi-box-arrow-right'
      };
    else dutyPill = { kind: 'other', label: s.text, icon: 'bi bi-info-circle-fill' };

    return { ...dutyPill, sessions, lateEarly };
  };

  const attendanceTagsFromRow = (row) => {
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    const tags = [];
    const lateEarlyTypes = getLateEarlyLeaveTypes(row);

    lateEarlyTypes.forEach((t) => {
      const match = leaves.find((l) => l?.leaveType === t);
      const vt = String(match?.violationType || '').toUpperCase();
      let tone = 'violation';
      if (
        vt === 'EARLY_EXIT' ||
        (isLateOrEarlyLeaveType(t) && String(t).toUpperCase().includes('EARLY'))
      ) {
        tone = 'violation-exit';
      } else if (
        vt === 'LATE_ARRIVAL' ||
        (isLateOrEarlyLeaveType(t) && String(t).toUpperCase().includes('LATE'))
      ) {
        tone = 'violation-late';
      }
      tags.push({ text: String(t).toUpperCase(), tone });
    });

    const leave = leaveLabelFromRow(row);
    if (leave?.isHalfDay) {
      tags.push({ text: 'HALF DAY', tone: 'half' });
    }

    return tags;
  };

  const renderSessionHalves = (sessions) => {
    if (!sessions) return null;
    return (
      <div className="attendance-session-halves" title="Session halves">
        <span
          className={`attendance-session-half attendance-session-half--${sessionHalfTone(
            sessions.firstHalf
          )}`}
        >
          <span className="attendance-session-half__kicker">1st</span>
          {sessions.firstHalf}
        </span>
        <span
          className={`attendance-session-half attendance-session-half--${sessionHalfTone(
            sessions.secondHalf
          )}`}
        >
          <span className="attendance-session-half__kicker">2nd</span>
          {sessions.secondHalf}
        </span>
      </div>
    );
  };

  const renderLeaveTypeChips = (types, row) => {
    if (!types?.length) return null;
    const leaves = Array.isArray(row?.leaveInfo?.leaves) ? row.leaveInfo.leaves : [];
    return (
      <div className="attendance-leave-types">
        {types.map((type) => {
          const match = leaves.find((l) => l?.leaveType === type);
          const half = Boolean(match?.isHalfDay);
          return (
            <span
              key={type}
              className={`attendance-leave-type${half ? ' attendance-leave-type--half' : ''}`}
              title={half ? `${type} · ${match.sessionType || 'Half Day'}` : type}
            >
              {type}
              {half ? (
                <span className="attendance-leave-type__half">
                  {match.sessionType === 'First Half'
                    ? '1H'
                    : match.sessionType === 'Second Half'
                      ? '2H'
                      : '½'}
                </span>
              ) : null}
            </span>
          );
        })}
      </div>
    );
  };

  const renderLateEarlyChips = (types) => {
    if (!types?.length) return null;
    return (
      <div className="attendance-leave-types">
        {types.map((t) => (
          <span
            key={t}
            className={`attendance-leave-type attendance-leave-type--${
              String(t).toUpperCase().includes('EARLY') ? 'early' : 'late'
            }`}
          >
            {t}
          </span>
        ))}
      </div>
    );
  };

  const renderStatusCell = (row) => {
    const pill = statusPillFromRow(row);

    if (pill.kind === 'leave') {
      return (
        <div className="attendance-status attendance-status--stack">
          {hasViolationLeaveFromRow(row)
            ? renderViolationLeaveStatus(row)
            : renderLeaveTypeChips(pill.types, row) || renderLeaveStatusPill(pill.label, row)}
          {renderLateEarlyChips(pill.lateEarly)}
          {renderSessionHalves(pill.sessions)}
        </div>
      );
    }

    if (pill.kind === 'wfh') {
      return (
        <div className="attendance-status attendance-status--stack">
          <span className="attendance-status__pill attendance-status__pill--wfh">
            <span>{pill.label}</span>
          </span>
          {renderLateEarlyChips(pill.lateEarly)}
          {renderSessionHalves(pill.sessions)}
        </div>
      );
    }

    if (pill.kind === 'wfh-leave') {
      return (
        <div className="attendance-status attendance-status--stack">
          <div className="attendance-status__chips">
            <span className="attendance-status__pill attendance-status__pill--wfh">
              <span>{pill.wfhLabel}</span>
            </span>
            {hasViolationLeaveFromRow(row)
              ? renderViolationLeaveStatus(row)
              : renderLeaveTypeChips(pill.types, row) ||
                renderLeaveStatusPill(pill.leaveLabel, row)}
          </div>
          {renderLateEarlyChips(pill.lateEarly)}
          {renderSessionHalves(pill.sessions)}
        </div>
      );
    }

    // Late/Early-only day (approved Late Arrival / Early Exit with check-in)
    if (
      pill.lateEarly?.length &&
      (pill.kind === 'out' ||
        pill.kind === 'duty' ||
        pill.kind === 'break' ||
        pill.kind === 'other')
    ) {
      return (
        <div className="attendance-status attendance-status--stack">
          {renderLateEarlyChips(pill.lateEarly)}
          {pill.kind === 'duty' || pill.kind === 'break' ? renderStatusPill(pill) : null}
          {renderSessionHalves(pill.sessions)}
        </div>
      );
    }

    return (
      <div className="attendance-status attendance-status--stack">
        {renderStatusPill(pill)}
        {renderLateEarlyChips(pill.lateEarly)}
        {renderSessionHalves(pill.sessions)}
      </div>
    );
  };

  const renderDateCell = (row) => {
    const label =
      row?.dateLabel ||
      (row?.date ? formatDate(moment(String(row.date), 'YYYYMMDD'), 'DD-MMM-YYYY') : '—');
    const tags = attendanceTagsFromRow(row);
    const isViolationRow = hasViolationLeaveFromRow(row);

    return (
      <div className={`attendance-date${isViolationRow ? ' attendance-date--violation' : ''}`}>
        <div className="attendance-date__text">{label}</div>
        {tags?.length ? (
          <div className="attendance-date__tags" title={tags.map((t) => t.text).join(', ')}>
            {tags.slice(0, 2).map((t) => (
              <span
                key={`${t.tone}-${t.text}`}
                className={`attendance-tag attendance-tag--${t.tone}`}
              >
                {t.tone.startsWith('violation') ? (
                  <i className="bi bi-exclamation-circle-fill attendance-tag__icon" aria-hidden />
                ) : null}
                {t.text}
              </span>
            ))}
            {tags.length > 2 ? (
              <span className="attendance-tag attendance-tag--more">+{tags.length - 2}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const handleChange = (dates) => {
    const [start, end] = Array.isArray(dates) ? dates : [null, null];
    const nextRange = [
      start ? moment(start).format('YYYYMMDD') : null,
      end ? moment(end).format('YYYYMMDD') : null
    ];
    setHasDateSelection(Boolean(nextRange[0]) || Boolean(nextRange[1]));
    dispatch(setRange(nextRange));
  };

  const handleClearDateRange = () => {
    dispatch(setRange([null, null]));
    setDebouncedRange([null, null]);
    setHasDateSelection(false);
  };

  const hasDateRange = hasDateSelection;

  const { totalWorked, totalBreak, employeeCount } = useMemo(() => {
    let totalWorkMs = 0;
    let totalBreakMs = 0;

    employeeGroups.forEach((group) => {
      if (group.summary) {
        totalWorkMs += group.summary.totalWorkMilliseconds || 0;
        totalBreakMs += group.summary.totalBreakMilliseconds || 0;
      } else {
        (group.attendance || []).forEach((row) => {
          totalWorkMs += row.totalWorkMilliseconds || 0;
          totalBreakMs += row.totalBreakMilliseconds || 0;
        });
      }
    });

    return {
      totalWorked: formatterTime(totalWorkMs / 1000),
      totalBreak: formatterTime(totalBreakMs / 1000),
      employeeCount: employeeGroups.length
    };
  }, [employeeGroups]);

  const formatSummaryHours = (group) => {
    if (group?.summary?.totalWorkingHours) return group.summary.totalWorkingHours;
    const ms = (group.attendance || []).reduce(
      (acc, row) => acc + (row.totalWorkMilliseconds || 0),
      0
    );
    return formatterTime(ms / 1000);
  };

  const openSession = (row) => {
    if (!row?._id) return;
    dispatch(setSelectedSession(row._id));
    dispatch(setSessionShow(true));
  };

  const pending = isLoading || isFetching;

  return (
    <div className="attendance-list mt-4">
      <div className="card shadow-sm border-0 attendance-list__card">
        <div className="card-header attendance-list__toolbar">
          <div className="attendance-list__summary">
            <div className="attendance-list__summaryItem">
              <div className="attendance-list__summaryLabel">Employees</div>
              <div className="attendance-list__summaryValue">{employeeCount}</div>
            </div>
            <div className="attendance-list__summaryItem">
              <div className="attendance-list__summaryLabel">Total Work</div>
              <div className="attendance-list__summaryValue">{totalWorked}</div>
            </div>
            <div className="attendance-list__summaryItem">
              <div className="attendance-list__summaryLabel">Total Break</div>
              <div className="attendance-list__summaryValue">{totalBreak}</div>
            </div>
          </div>

          <div className="attendance-list__controls">
            <div className="attendance-list__date attendance-list__date--has-clear">
              <span className="attendance-list__dateIcon" aria-hidden>
                <i className="bi bi-calendar3" />
              </span>
              <div className="attendance-list__datePickerWrap">
                <DatePicker
                  selected={range[0] ? moment.utc(range[0], 'YYYYMMDD').toDate() : null}
                  onChange={handleChange}
                  startDate={range[0] ? moment.utc(range[0], 'YYYYMMDD').toDate() : null}
                  endDate={range[1] ? moment.utc(range[1], 'YYYYMMDD').toDate() : null}
                  selectsRange
                  monthsShown={2}
                  withPortal
                  showPopperArrow={false}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select date range"
                  className="form-control attendance-list__dateInput"
                  calendarClassName="attendance-datepicker"
                  popperClassName="attendance-datepicker-popper"
                  popperPlacement="bottom-end"
                  maxDate={new Date()}
                />
              </div>
              <button
                type="button"
                className={`attendance-list__dateClear${hasDateRange ? '' : ' attendance-list__dateClear--inactive'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasDateRange) handleClearDateRange();
                }}
                disabled={!hasDateRange}
                aria-label="Clear date range"
                title="Clear date range"
              >
                <i className="icofont-close" aria-hidden />
              </button>
            </div>

            <button
              type="button"
              className="btn btn-outline-primary btn-sm attendance-list__refresh"
              onClick={() => refetch()}
            >
              <i className="bi bi-arrow-repeat" aria-hidden /> Refresh
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          {pending ? (
            <div className="attendance-list__table py-3">
              <TableSkeleton columns={6} />
            </div>
          ) : !employeeGroups.length ? (
            <div className="py-4">
              <EmptyData />
            </div>
          ) : (
            <div className="hrx-att-accordion-wrap">
              <Accordion
                activeKey={activeKeys}
                onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : keys ? [keys] : [])}
                alwaysOpen
              >
                {employeeGroups.map((group) => {
                  const eventKey = String(group.userId);
                  const summary = group.summary || {};
                  const days = group.attendance || [];

                  return (
                    <Accordion.Item
                      key={eventKey}
                      eventKey={eventKey}
                      className="hrx-att-accordion-item"
                    >
                      <Accordion.Header>
                        <div className="hrx-att-acc-head">
                          <div className="hrx-att-acc-identity">
                            <span className="hrx-att-acc-avatar" aria-hidden>
                              {(group.userName || '?').charAt(0).toUpperCase()}
                            </span>
                            <div className="hrx-att-acc-identity__text">
                              <div className="hrx-att-acc-name">{group.userName}</div>
                              <div className="hrx-att-acc-sub">
                                {summary.totalDays ?? days.length} day
                                {(summary.totalDays ?? days.length) === 1 ? '' : 's'}
                              </div>
                            </div>
                          </div>

                          <div className="hrx-att-acc-metrics">
                            <span className="hrx-att-pill hrx-att-pill--present">
                              <span className="hrx-att-pill__label">Present</span>
                              <span className="hrx-att-pill__value">
                                {summary.presentDays ?? '—'}
                              </span>
                            </span>
                            <span className="hrx-att-pill hrx-att-pill--leave">
                              <span className="hrx-att-pill__label">Leave</span>
                              <span className="hrx-att-pill__value">
                                {summary.leaveDays ?? '—'}
                              </span>
                            </span>
                            <span className="hrx-att-pill hrx-att-pill--absent">
                              <span className="hrx-att-pill__label">Absent</span>
                              <span className="hrx-att-pill__value">
                                {summary.absentDays ?? '—'}
                              </span>
                            </span>
                            <span className="hrx-att-pill hrx-att-pill--work">
                              <span className="hrx-att-pill__label">Work</span>
                              <span className="hrx-att-pill__value">
                                {formatSummaryHours(group)}
                              </span>
                            </span>
                          </div>
                        </div>
                      </Accordion.Header>

                      <Accordion.Body>
                        {!days.length ? (
                          <div className="hrx-att-empty-days">No attendance records.</div>
                        ) : (
                          <div className="hrx-att-day-table-wrap">
                            <table className="hrx-att-day-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Check-In</th>
                                  <th>Check-Out</th>
                                  <th>Status</th>
                                  <th>Worked</th>
                                  <th>Break</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {days.map((row, idx) => {
                                  const checkOut = getHrxDisplayCheckOutTime(row);
                                  const rowKey = row._id || `${group.userId}-${row.date || idx}`;
                                  const canView = Boolean(row._id);

                                  return (
                                    <tr
                                      key={rowKey}
                                      className={
                                        hasViolationLeaveFromRow(row)
                                          ? 'hrx-att-day-table__row--violation'
                                          : undefined
                                      }
                                    >
                                      <td>{renderDateCell(row)}</td>
                                      <td>
                                        {row?.checkInTime
                                          ? formatTimestampInTimezone(row.checkInTime, timeZone)
                                          : '—'}
                                      </td>
                                      <td>
                                        {checkOut
                                          ? formatTimestampInTimezone(checkOut, timeZone)
                                          : '—'}
                                      </td>
                                      <td>{renderStatusCell(row)}</td>
                                      <td>
                                        {row?.totalWorkingHours ||
                                          formatterTime((row?.totalWorkMilliseconds || 0) / 1000)}
                                      </td>
                                      <td>
                                        {formatterTime((row?.totalBreakMilliseconds || 0) / 1000)}
                                      </td>
                                      <td>
                                        <button
                                          type="button"
                                          className="attendance-list__viewBtn"
                                          onClick={() => openSession(row)}
                                          disabled={!canView}
                                          aria-label="View attendance details"
                                          title={canView ? 'View details' : 'No session'}
                                        >
                                          <i className="bi bi-eye" aria-hidden />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </div>
          )}
        </div>

        <SessionDialog />
      </div>
    </div>
  );
};

export default AttendanceTable;
